/**
 * Button Component
 * Uses Uber Base design tokens
 */

import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '../../utils/helpers'
import { Loader2 } from 'lucide-react'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
  loading?: boolean // Alias for isLoading
  fullWidth?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      loading = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const isLoadingState = isLoading || loading
    
    const baseStyles = cn(
      'inline-flex items-center justify-center gap-2',
      'font-weight-medium rounded-sm',
      'transition-colors duration-150',
      'focus:outline-none focus:ring-2 focus:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      fullWidth && 'w-full'
    )

    const variants = {
      primary: cn(
        'bg-uber-black text-uber-white',
        'hover:bg-gray-800',
        'focus:ring-uber-black'
      ),
      secondary: cn(
        'bg-gray-100 text-content-primary',
        'hover:bg-gray-200',
        'focus:ring-gray-400'
      ),
      outline: cn(
        'bg-transparent text-content-primary',
        'border border-border-default',
        'hover:bg-gray-50',
        'focus:ring-gray-400'
      ),
      ghost: cn(
        'bg-transparent text-content-primary',
        'hover:bg-gray-100',
        'focus:ring-gray-400'
      ),
      danger: cn(
        'bg-negative text-uber-white',
        'hover:bg-red-700',
        'focus:ring-negative'
      ),
    }

    const sizes = {
      sm: 'h-8 px-3 text-label-xsmall',
      md: 'h-10 px-4 text-label-small',
      lg: 'h-12 px-6 text-label-medium',
    }

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || isLoadingState}
        {...props}
      >
        {isLoadingState ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          leftIcon
        )}
        {children}
        {!isLoadingState && rightIcon}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }
